import { defineMessages, type MessageDescriptor } from 'react-intl';
import type {
  ProfileSendFailCode,
  ProfileSendFileRefusal,
  ProfileSendItem,
  ProfileSendService,
  ProfileSendUnavailable,
} from 'fym-shared/src/music24/profileSend';

/** The names, sizes and reason lines of the items of "Send to all" (design profile-card c). */
export const sendAllItemMessages = defineMessages({
  header: { id: 'app.sml.promote.profile.sendAll.item.header', defaultMessage: 'Header' },
  artistPhoto: { id: 'app.sml.promote.profile.sendAll.item.artistPhoto', defaultMessage: 'Artist photo' },
  profilePicture: { id: 'app.sml.promote.profile.sendAll.item.profilePicture', defaultMessage: 'Profile picture' },
  picture: { id: 'app.sml.promote.profile.sendAll.item.picture', defaultMessage: 'Picture' },
  banner: { id: 'app.sml.promote.profile.sendAll.item.banner', defaultMessage: 'Banner' },
  cover: { id: 'app.sml.promote.profile.sendAll.item.cover', defaultMessage: 'Cover' },
  bio: { id: 'app.sml.promote.profile.sendAll.item.bio', defaultMessage: 'Bio' },
  channelDescription: {
    id: 'app.sml.promote.profile.sendAll.item.channelDescription',
    defaultMessage: 'Channel description',
  },
  about: { id: 'app.sml.promote.profile.sendAll.item.about', defaultMessage: 'About' },
  facebookLink: { id: 'app.sml.promote.profile.sendAll.item.facebookLink', defaultMessage: 'Facebook link' },
  imageSize: {
    id: 'app.sml.promote.profile.sendAll.item.imageSize',
    defaultMessage: '{width} x {height} · version {version}',
  },
  imageSizeNone: { id: 'app.sml.promote.profile.sendAll.item.imageSizeNone', defaultMessage: '{width} x {height}' },
  bioLong: {
    id: 'app.sml.promote.profile.sendAll.item.bioLong',
    defaultMessage: 'The long bio · {count} of {max} characters',
  },
  bioYoutube: {
    id: 'app.sml.promote.profile.sendAll.item.bioYoutube',
    defaultMessage: 'The long bio, cut to 1000 characters',
  },
  bioFacebook: { id: 'app.sml.promote.profile.sendAll.item.bioFacebook', defaultMessage: 'The short bio' },
  bioDeezer: { id: 'app.sml.promote.profile.sendAll.item.bioDeezer', defaultMessage: 'The long bio' },
  linkDeezer: {
    id: 'app.sml.promote.profile.sendAll.item.linkDeezer',
    defaultMessage: 'The link of your connected Facebook Page',
  },
  whyReconnect: { id: 'app.sml.promote.profile.sendAll.why.reconnect', defaultMessage: 'Sign in again to send.' },
  whyNeedsScope: {
    id: 'app.sml.promote.profile.sendAll.why.needsScope',
    defaultMessage: 'Sign in again and let MUSIC24 edit the profile.',
  },
  whyNoAutoUpload: {
    id: 'app.sml.promote.profile.sendAll.why.noAutoUpload',
    defaultMessage: 'Ask MUSIC24 support to turn on auto upload for this artist.',
  },
  whyNothingImage: { id: 'app.sml.promote.profile.sendAll.why.nothingImage', defaultMessage: 'Make it first.' },
  whyNothingBio: { id: 'app.sml.promote.profile.sendAll.why.nothingBio', defaultMessage: 'Write your bio first.' },
  whyPreview: {
    id: 'app.sml.promote.profile.sendAll.why.preview',
    defaultMessage: 'Upgrade to send. A Free file is a preview with the mark.',
  },
  whyTooSmall: {
    id: 'app.sml.promote.profile.sendAll.why.tooSmall',
    defaultMessage: 'The file is too small for {service}.',
  },
  whyTooLarge: {
    id: 'app.sml.promote.profile.sendAll.why.tooLarge',
    defaultMessage: 'The file is too large for {service}.',
  },
  whyType: { id: 'app.sml.promote.profile.sendAll.why.type', defaultMessage: '{service} takes JPEG or PNG only.' },
  whyShape: {
    id: 'app.sml.promote.profile.sendAll.why.shape',
    defaultMessage: 'The file has the wrong shape for {service}.',
  },
  whyNoLink: { id: 'app.sml.promote.profile.sendAll.why.noLink', defaultMessage: 'Connect a Facebook Page first.' },
  failReconnect: {
    id: 'app.sml.promote.profile.sendAll.fail.reconnect',
    defaultMessage: '{service} refused the sign-in. Sign in again.',
  },
  failNoPermission: {
    id: 'app.sml.promote.profile.sendAll.fail.noPermission',
    defaultMessage: 'Your sign-in cannot edit this profile. Ask for the Editor role.',
  },
  failRefusedDetail: {
    id: 'app.sml.promote.profile.sendAll.fail.refusedDetail',
    defaultMessage: '{service} said: “{detail}”',
  },
  failRefused: { id: 'app.sml.promote.profile.sendAll.fail.refused', defaultMessage: '{service} refused it.' },
  failFile: {
    id: 'app.sml.promote.profile.sendAll.fail.file',
    defaultMessage: '{service} refused the file. Make a full-size file, then retry.',
  },
  failFileGone: {
    id: 'app.sml.promote.profile.sendAll.fail.fileGone',
    defaultMessage: 'The file is gone. Make it again, then retry.',
  },
  failReadOnly: {
    id: 'app.sml.promote.profile.sendAll.fail.readOnly',
    defaultMessage: 'Deezer shows its own bio. Replace it there first, then send again.',
  },
  failUnknown: {
    id: 'app.sml.promote.profile.sendAll.fail.unknown',
    defaultMessage: '{service} gave an answer that we do not know. Nothing was changed.',
  },
  failUnsure: {
    id: 'app.sml.promote.profile.sendAll.fail.unsure',
    defaultMessage: 'We do not know if it went. Look at the profile, then retry.',
  },
  failError: {
    id: 'app.sml.promote.profile.sendAll.fail.error',
    defaultMessage: 'Something went wrong. Nothing was changed.',
  },
});

const m = sendAllItemMessages;

/** The name of an item on its service row. Pure. */
export function itemLabel(service: ProfileSendService, item: ProfileSendItem): MessageDescriptor {
  switch (item) {
    case 'header':
      return m.header;
    case 'picture':
      if (service === 'spotify') {
        return m.artistPhoto;
      }
      return service === 'facebook' ? m.profilePicture : m.picture;
    case 'banner':
      return m.banner;
    case 'cover':
      return m.cover;
    case 'bio':
      if (service === 'youtube') {
        return m.channelDescription;
      }
      return service === 'facebook' ? m.about : m.bio;
    case 'facebookLink':
      return m.facebookLink;
  }
}

/** The reason line of an item that cannot go now, or null. Pure. */
export function unavailableLine(
  item: ProfileSendItem,
  reason: ProfileSendUnavailable | null,
  refusal: ProfileSendFileRefusal | null
): MessageDescriptor | null {
  switch (reason) {
    case 'reconnect':
      return m.whyReconnect;
    case 'needsScope':
      return m.whyNeedsScope;
    case 'noAutoUploadAccess':
      return m.whyNoAutoUpload;
    case 'nothingReady':
      return item === 'bio' ? m.whyNothingBio : m.whyNothingImage;
    case 'preview':
      return m.whyPreview;
    case 'noLink':
      return m.whyNoLink;
    case 'fileRefused':
      if (refusal === 'preview') {
        return m.whyPreview;
      }
      if (refusal === 'tooLarge') {
        return m.whyTooLarge;
      }
      if (refusal === 'type') {
        return m.whyType;
      }
      return refusal === 'shape' ? m.whyShape : m.whyTooSmall;
    case 'notConnected':
    case 'notAvailableYet':
    case 'busy':
    case null:
      return null;
  }
}

/** The line of a failed item. `refused` with a text of the service quotes it. Pure. */
export function failLine(code: ProfileSendFailCode, detail: string | null): MessageDescriptor {
  switch (code) {
    case 'reconnect':
      return m.failReconnect;
    case 'noPermission':
      return m.failNoPermission;
    case 'refused':
      return detail !== null && detail !== '' ? m.failRefusedDetail : m.failRefused;
    case 'fileRefused':
      return m.failFile;
    case 'fileGone':
      return m.failFileGone;
    case 'readOnlyOnService':
      return m.failReadOnly;
    case 'unknownAnswer':
      return m.failUnknown;
    case 'unsure':
      return m.failUnsure;
    case 'error':
      return m.failError;
  }
}
